import { useState, useMemo } from 'react';
import { periodLabel } from '../utils/financialPeriods';

// =============================================================================
// usePeriodDrilldown
// Encapsulates the year → quarter → month column-expansion behaviour so any
// period-based grid can opt in. Flat grids simply don't call this.
//
// Input:  periods — sorted array of period numbers present in the data, e.g.
//                   [1,2,3,...,12]. Caller derives this from its own rows.
//
// Returns:
//   visibleCols   — array of column descriptors the caller maps to columns/cells:
//                     { kind: 'year',    periods }
//                     { kind: 'quarter', q, periods }
//                     { kind: 'month',   period, q }
//   quarters      — [{ q, periods }] (useful for expand-all)
//   yearExpanded, expandedQuarters  — current state
//   toggleYear, toggleQuarter       — click handlers
//   expandAll, collapseAll          — bulk handlers
//
// The caller still owns rendering (it knows its own column widths, labels,
// and how to sum a period) — this hook only manages *which* columns show.
// =============================================================================
export function usePeriodDrilldown(periods) {
  const [yearExpanded, setYearExpanded] = useState(false);
  const [expandedQuarters, setExpandedQuarters] = useState({}); // { 1: true }

  const quarters = useMemo(() => {
    const map = {};
    periods.forEach((p) => {
      const q = Math.floor((p - 1) / 3) + 1;
      if (!map[q]) map[q] = [];
      map[q].push(p);
    });
    return Object.entries(map)
      .map(([q, ps]) => ({ q: Number(q), periods: ps }))
      .sort((a, b) => a.q - b.q);
  }, [periods]);

  const visibleCols = useMemo(() => {
    if (!yearExpanded) {
      return [{ kind: 'year', periods }];
    }
    const cols = [];
    quarters.forEach(({ q, periods: qPeriods }) => {
      if (expandedQuarters[q]) {
        qPeriods.forEach((p) => cols.push({ kind: 'month', period: p, q }));
      } else {
        cols.push({ kind: 'quarter', q, periods: qPeriods });
      }
    });
    return cols;
  }, [yearExpanded, quarters, periods, expandedQuarters]);

  const toggleYear = () => setYearExpanded((v) => !v);
  const toggleQuarter = (q) =>
    setExpandedQuarters((prev) => ({ ...prev, [q]: !prev[q] }));

  const expandAll = () => {
    setYearExpanded(true);
    setExpandedQuarters(
      quarters.reduce((acc, { q }) => { acc[q] = true; return acc; }, {})
    );
  };
  const collapseAll = () => {
    setYearExpanded(false);
    setExpandedQuarters({});
  };

  return {
    visibleCols,
    quarters,
    yearExpanded,
    expandedQuarters,
    toggleYear,
    toggleQuarter,
    expandAll,
    collapseAll,
  };
}

// =============================================================================
// The rendering half, shared so that every period grid in the product drills
// down identically. Before this, OPEX and the P&L each hand-rolled the same
// three column shapes with their own labels, widths and chevrons — and every
// other monthly grid simply showed twelve columns and a horizontal scrollbar.
//
//   const drill = usePeriodDrilldown(PERIODS);
//   const gcols = [...fixed, ...periodColumns(drill, fyStartMonth), ...tail];
//   // in getCellContent:  const ps = periodsOfCol(gcols, id)  -> [1,2,3] | [4]
//   // in onHeaderClicked: periodHeaderToggle(drill, colIds[col])
//
// A column covering ONE period is the editable month cell; a column covering
// several is an aggregate and must be read-only — there is no sane way to type
// into "Q2".
// =============================================================================

// `agg` is the page's call and it is not cosmetic: twelve monthly COSTS roll up
// by SUM, twelve monthly PRICES or INDEX LEVELS roll up by MEAN. A price grid
// that shows the sum of twelve prices as "Full Year" is showing a number that
// exists nowhere in the business.
export const periodColumns = (drill, fyStartMonth, opts = {}) => {
  const { monthWidth = 80, groupWidth = 104, agg = 'sum' } = opts;
  const suffix = agg === 'avg' ? ' avg' : '';
  return drill.visibleCols.map((c) => {
    if (c.kind === 'year') {
      return {
        id: 'YEAR',
        title: `${drill.yearExpanded ? '▾' : '▸'} Full Year${suffix}`,
        width: groupWidth,
        periods: c.periods,
      };
    }
    if (c.kind === 'quarter') {
      return {
        id: `Q${c.q}`,
        title: `${drill.expandedQuarters[c.q] ? '▾' : '▸'} Q${c.q}${suffix}`,
        width: Math.max(88, groupWidth - 16),
        periods: c.periods,
      };
    }
    return {
      id: `P${c.period}`,
      title: periodLabel(c.period, fyStartMonth),
      width: monthWidth,
      periods: [c.period],
    };
  });
};

// The periods a column covers, or null if it is not a period column at all.
export const periodsOfCol = (columns, colId) =>
  columns.find((c) => c.id === colId)?.periods || null;

// Header click → expand/collapse. Returns true when it handled the click, so a
// page with its own header behaviour can fall through to it.
export const periodHeaderToggle = (drill, colId) => {
  if (colId === 'YEAR') { drill.toggleYear(); return true; }
  if (/^Q\d+$/.test(colId)) { drill.toggleQuarter(Number(colId.slice(1))); return true; }
  return false;
};

// Roll a set of periods up out of a 1-based lookup (a map or a function).
export const aggPeriods = (periods, read, agg = 'sum') => {
  const vals = periods.map((p) => Number(typeof read === 'function' ? read(p) : read?.[p]) || 0);
  if (!vals.length) return 0;
  return agg === 'avg' ? vals.reduce((a, b) => a + b, 0) / vals.length : vals.reduce((a, b) => a + b, 0);
};

export default usePeriodDrilldown;
